"use client";

/**
 * One FAQ accordion item. Native <details> (content stays in the server-rendered
 * HTML for SEO + works with no JS), with a thin client wrapper that fires
 * `faq_expand` the first time it's opened. The question text is public marketing
 * copy — no customer/BOM/MPN/PII is ever sent.
 */
import * as React from "react";
import posthog from "posthog-js";
import type { SeoPageType } from "@/lib/analytics/events";

export function FaqDetails({
  index,
  q,
  a,
  sourcePage,
  pageType,
}: {
  index: number;
  q: string;
  a: string;
  sourcePage?: string;
  pageType?: SeoPageType;
}) {
  const firedRef = React.useRef(false);
  return (
    <details
      className="group border-b rule"
      onToggle={(e) => {
        if ((e.currentTarget as HTMLDetailsElement).open && !firedRef.current) {
          firedRef.current = true;
          try {
            posthog.capture("faq_expand", {
              source_page: sourcePage,
              page_type: pageType,
              cta_text: q,
            });
          } catch {
            /* analytics must never break the page */
          }
        }
      }}
    >
      <summary className="py-5 flex items-center gap-4 cursor-pointer list-none">
        <span className="font-mono text-[11px] ink-3 w-8">
          {String(index + 1).padStart(2, "0")}
        </span>
        <span className="font-display font-medium text-[16px] lg:text-[17px] flex-1 leading-snug">
          {q}
        </span>
        <span className="font-mono text-[14px] ink-3 group-open:rotate-45 transition-transform">
          ＋
        </span>
      </summary>
      <div className="pl-12 pr-8 pb-5 text-[14px] ink-2 leading-relaxed max-w-[68ch]">
        {a}
      </div>
    </details>
  );
}
